"use client";

import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Language } from "@linqx/i18n";
import { LogoWithText } from "@/components/logo";
import { BlurFade } from "@/components/magicui/blur-fade";
import { DotPattern } from "@/components/magicui/dot-pattern";
import { cn } from "@/lib/utils";

interface LegalLayoutProps {
  title: string;
  lastUpdated: string;
  children: React.ReactNode;
}

export function LegalLayout({
  title,
  lastUpdated,
  children,
}: LegalLayoutProps) {
  return (
    <main className="min-h-screen bg-background overflow-hidden">
      {/* Background pattern */}
      <DotPattern
        className={cn(
          "[mask-image:radial-gradient(600px_circle_at_center,white,transparent)]",
          "fill-primary/10 fixed inset-0",
        )}
      />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 md:h-20">
            <a href="/" className="flex items-center gap-2 group">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <LogoWithText size={28} />
              </motion.div>
            </a>

            <a
              href="/"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              {Language.getKey("landing_legal_back_home")}
            </a>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="relative z-10 pt-32 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <BlurFade delay={0.1} inView>
              <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
                <span className="gradient-text">{title}</span>
              </h1>
              <p className="text-muted-foreground mb-12">
                {Language.replaceVarsString(
                  Language.getKey("landing_legal_last_updated"),
                  { date: lastUpdated },
                )}
              </p>
            </BlurFade>

            <BlurFade delay={0.2} inView>
              <div className="prose prose-invert prose-lg max-w-none">
                {children}
              </div>
            </BlurFade>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 border-t border-border/30 py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              {Language.replaceVarsString(
                Language.getKey("landing_footer_rights"),
                { year: String(new Date().getFullYear()) },
              )}
            </p>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <a
                href="/privacy"
                className="hover:text-foreground transition-colors"
              >
                {Language.getKey("landing_footer_link_privacy")}
              </a>
              <a
                href="/terms"
                className="hover:text-foreground transition-colors"
              >
                {Language.getKey("landing_footer_link_terms")}
              </a>
              <a
                href="/eula"
                className="hover:text-foreground transition-colors"
              >
                {Language.getKey("landing_footer_link_eula")}
              </a>
            </div>
          </div>
        </div>
      </footer>
    </main>
  );
}
